/**
 * Notification bridge — connects data sources to the client-side notification store.
 *
 * Two-pronged approach:
 *   1. REST API: Fetches persisted notifications from the server on startup + periodic refresh.
 *   2. SignalR: Receives real-time push for instant display (NotificationReceived event).
 *
 * The server is now the source of truth (notifications table in life4earth_chat DB).
 * The client store is a cache for instant UI rendering.
 */
import { onChatEvent } from '../api/chatConnection';
import { fetchNotifications } from '../api/chat';
import type { NotificationApiResponse } from '../api/chat';
import { addNotification, bulkLoadNotifications } from './notificationStore';
import type { AppNotification, NotificationType } from './types';

let cleanupFns: Array<() => void> = [];
let pollInterval: ReturnType<typeof setInterval> | null = null;

/** Safely parse a JSONB metadata string. */
function parseMetadata(raw: string | null | undefined): Record<string, any> | undefined {
  if (!raw) return undefined;
  try { return JSON.parse(raw); } catch { return undefined; }
}

/** Map a server notification to the client-side AppNotification type. */
function toAppNotification(n: NotificationApiResponse): AppNotification {
  return {
    id: n.id,
    type: (n.type || 'system') as NotificationType,
    title: n.actorDisplayName || n.title,
    body: n.body || undefined,
    timestamp: n.createdAt,
    isRead: n.isRead,
    href: n.href || undefined,
    dedupeKey: `${n.type}:${n.referenceId || n.id}`,
    avatarUrl: n.actorProfileImageUrl || undefined,
    actorName: n.actorDisplayName || undefined,
    referenceId: n.referenceId || undefined,
    referenceType: n.referenceType || undefined,
    metadata: parseMetadata(n.metadata),
  };
}

/**
 * Start feeding events into the notification store.
 * Call once on app mount. Returns a cleanup function.
 */
export function startNotificationBridge(_currentUserGuid: string): () => void {
  // ── 1. Load persisted notifications from the server ──
  const loadFromServer = () => {
    fetchNotifications(50)
      .then((serverNotifs: NotificationApiResponse[]) => {
        // Server returns newest-first. bulkLoadNotifications preserves that
        // order and handles dedup + active-conversation suppression in one pass.
        bulkLoadNotifications(serverNotifs.map(toAppNotification));
      })
      .catch(() => { /* silent — server may not have notification tables yet */ });
  };

  // Initial load
  loadFromServer();
  // Re-poll every 60s to catch anything missed (belt + suspenders with SignalR)
  pollInterval = setInterval(loadFromServer, 60000);

  // ── 2. Real-time push via SignalR ──
  // The server sends "NotificationReceived" when a new notification is created.
  const unsubRealtime = onChatEvent(
    'NotificationReceived',
    (data: any) => {
      const notif: AppNotification = {
        id: data.id,
        type: (data.type || 'system') as NotificationType,
        title: data.title || 'Notification',
        body: data.body || undefined,
        timestamp: data.createdAt || new Date().toISOString(),
        isRead: false,
        href: data.href || undefined,
        dedupeKey: `${data.type}:${data.referenceId || data.id}`,
        avatarUrl: undefined, // Will be enriched on next server fetch
        actorName: data.title || undefined,
        referenceId: data.referenceId || undefined,
        referenceType: data.referenceType || undefined,
        metadata: parseMetadata(data.metadata),
      };
      addNotification(notif);
    }
  );
  cleanupFns.push(unsubRealtime);

  // Return cleanup
  return () => {
    cleanupFns.forEach((fn) => fn());
    cleanupFns = [];
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };
}
